import React from 'react'

/**
 * Reads the SMS one-time code via the Web OTP API and hands it to `onSuccess`.
 *
 * Chrome/Edge on Android only — iOS exposes no SMS read API, so there the
 * `autoComplete="one-time-code"` hint on the inputs gives a one-tap keyboard
 * suggestion instead. Unsupported browsers fall through silently.
 *
 * Requires the SMS to end with an origin-bound line, e.g.
 *   @shanishenhav.online #12345
 * Without that last line the browser never resolves the request. See
 * AuthController::SMS_ORIGIN_TAG on the backend.
 *
 * The callback is held in a ref on purpose: callers pass an inline arrow, so a
 * dependency on it would re-run this effect on every render, aborting and
 * re-issuing the pending request each time — which prevents it ever resolving.
 */
function useWebOTP(onSuccess: (code: string) => void) {
  const callbackRef = React.useRef(onSuccess)

  React.useEffect(() => {
    callbackRef.current = onSuccess
  }, [onSuccess])

  React.useEffect(() => {
    if (!('OTPCredential' in window)) {
      return
    }

    const abortController = new AbortController()

    void (async () => {
      try {
        const credential = await (navigator.credentials as any).get({
          otp: { transport: ['sms'] },
          signal: abortController.signal,
        })

        const code = credential?.code
        if (typeof code === 'string' && code.length > 0) {
          callbackRef.current(code)
        }
      } catch (err) {
        // Unmounting aborts the pending request; that is expected, not a fault.
        if ((err as Error)?.name !== 'AbortError') {
          console.error('[WEB OTP]', err)
        }
      }
    })()

    return () => {
      abortController.abort()
    }
  }, [])
}

export default useWebOTP
